import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Caption } from "@/components/ui/typography";
import { VBadge } from "@/components/ui/vBadge";
import { cn } from "@/components/ui/cn";

type VKpiTone = "primary" | "success" | "warning" | "error" | "info" | "neutral";

type VKpiCardProps = {
  label: string;
  value: ReactNode;
  helper?: string;
  icon?: ReactNode;
  tone?: VKpiTone;
  trend?: { value: string; direction: "up" | "down" | "flat" };
  onClick?: () => void;
  className?: string;
};

const trendColor = {
  up: "success",
  down: "error",
  flat: "neutral",
} as const;

const iconToneClass: Record<VKpiTone, string> = {
  primary: "bg-primary-50 text-primary-600",
  success: "bg-success-50 text-success-600",
  warning: "bg-warning-50 text-warning-600",
  error: "bg-error-50 text-error-600",
  info: "bg-info-50 text-info-600",
  neutral: "bg-neutral-100 text-neutral-500",
};

export function VKpiCard({
  label,
  value,
  helper,
  icon,
  tone = "neutral",
  trend,
  onClick,
  className,
}: VKpiCardProps): React.JSX.Element {
  const card = (
    <Card
      padding="md"
      className={cn("p-5", onClick && "h-full transition-shadow hover:shadow-md", className)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <Caption tone="muted" as="p" className="truncate font-medium">
            {label}
          </Caption>
          <p className="text-3xl font-semibold leading-none tracking-tight text-foreground tabular-nums">
            {value}
          </p>
        </div>
        {icon ? (
          <div
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-lg",
              iconToneClass[tone],
            )}
            aria-hidden
          >
            {icon}
          </div>
        ) : null}
      </div>
      {trend || helper ? (
        <div className="mt-3 flex items-center gap-2">
          {trend ? <VBadge color={trendColor[trend.direction]}>{trend.value}</VBadge> : null}
          {helper ? (
            <Caption tone="muted" className="truncate">
              {helper}
            </Caption>
          ) : null}
        </div>
      ) : null}
    </Card>
  );

  if (!onClick) return card;
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {card}
    </button>
  );
}

export type { VKpiCardProps };
